import { ServerSentEventClient } from '@goldcast/sse-client';

import { mattermostApiClient } from '../apiClient';
import loggerService from '../logger.service';
import trackerService from '../tracker.service';
import { shouldRefreshAuthToken, getAuthTokenStatus } from '../socketClient/authUtils';

import analytics from '@/analytics';
import store from '@/store';
import {
  sendRoomUpdateEvent,
  processUpdateConnectionStatus,
  processSocketOpenDone
} from '@/store/modules/socketMessageProcessor';

const MISSED_PING_THRESHOLD_TIME = 75 * 1000; // 1 minute 15 secs - we get server pings at every 60 secs
const ONE_MINUTE = 60 * 1000;
let lastMsgTimeStamp = 0;

export class mmEventSourceClient extends ServerSentEventClient {
  protected pingTimeout: NodeJS.Timeout | null = null;
  constructor(url: string, logger: typeof loggerService) {
    super(url, logger, 'mmServerSentEvent');
  }

  preConnect() {
    this.logger.info('Running SSE preConnect for mmsocket');
    trackerService.track('serverSentEvent', 'shouldBeUsed', { isEnabled: true });
    trackerService.track('serverSentEvent', 'connectionInitialized', { timestamp: Date.now() });
    store.commit('eventsource/setEventSourceStatus', 'connecting');
    processUpdateConnectionStatus({ status: 'connecting', firstConnectDone: store.state.eventsource.firstConnectDone });

    const authStatus = getAuthTokenStatus();
    loggerService.info(
      'SSE: preConnect - checking auth. hasToken:',
      authStatus.hasToken,
      'isJwtAuth:',
      authStatus.isJwt,
      'tokenExpired:',
      authStatus.tokenExpired
    );

    if (shouldRefreshAuthToken()) {
      loggerService.info('SSE: preConnect - token needs refresh, calling onUnauthorized');
      return Promise.resolve(mattermostApiClient.onUnauthorized()) as Promise<void>;
    }
    loggerService.info('SSE: preConnect - token is valid, proceeding with connection');
    return Promise.resolve();
  }

  onError(reconnectionAttempt: number) {
    if (reconnectionAttempt > 7) {
      trackerService.track('serverSentEvent', 'retry');
      analytics.track('sse-errored-out', {
        reconnectionAttempt,
        source: 'serverSentEvent',
        event_id: store.state.event.event?.id,
        role: store.state.user.currentEventUser?.event_role
      });
    }

    loggerService.warn('OnError called for server sent event: ', reconnectionAttempt);

    trackerService.track('serverSentEvent', 'error');
    store.commit('eventsource/setEventSourceStatus', 'error');
    return processUpdateConnectionStatus({
      status: 'error',
      firstConnectDone: store.state.eventsource.firstConnectDone
    });
  }

  onMessage(event: any) {
    lastMsgTimeStamp = Date.now();
    const msg = JSON.parse(event.data);
    if (msg?.event === 'hello') {
      store.dispatch('eventsource/setConnectionId', msg);
      store.commit('eventsource/setEventSourceStatus', 'online');
      processUpdateConnectionStatus({ status: 'online', firstConnectDone: store.state.eventsource.firstConnectDone });
      sendRoomUpdateEvent();
    }
    return store.dispatch('mattermost/processEvent', { msg: event?.data, source: 'SSE-mattermost' });
  }

  onPing() {
    loggerService.log('ping received');
    if (this.pingTimeout) {
      clearTimeout(this.pingTimeout);
    }
    this.trackingPings();
  }

  onClosed() {
    trackerService.track('serverSentEvent', 'disconnections');
    if (this.pingTimeout) {
      clearTimeout(this.pingTimeout);
    }
    store.commit('eventsource/setEventSourceStatus', 'closed');
    return processUpdateConnectionStatus({
      status: 'closed',
      firstConnectDone: store.state.eventsource.firstConnectDone
    });
  }

  onOpened() {
    trackerService.track('serverSentEvent', 'connectionEstablished', { timestamp: Date.now() });
    this.trackingPings();
    store.commit('eventsource/setEventSourceStatus', 'connected');
    processSocketOpenDone();
  }
  trackingPings() {
    // clear if any existing scheduled reconnection
    if (this.pingTimeout) {
      clearTimeout(this.pingTimeout);
    }
    // This we ll check try to reconnect if no pings are there from server in one minute 15 secs
    this.pingTimeout = setTimeout(() => {
      this.reconnnectSSEConnection();
    }, MISSED_PING_THRESHOLD_TIME);
  }
  reconnnectSSEConnection() {
    const isLastMMmsgOlderThanOneMinute = Date.now() - lastMsgTimeStamp > ONE_MINUTE;
    if (this.pingTimeout) {
      clearTimeout(this.pingTimeout);
    }
    if (isLastMMmsgOlderThanOneMinute) {
      loggerService.info(
        'SSE-mmsocket: missed ping plus we dont have any message in last one minute, reconnecting',
        this.eventSource?.readyState
      );
      store
        .dispatch('eventsource/close')
        .then(() => store.dispatch('eventsource/connect'))
        .catch(err => loggerService.error('SSE: failed to reconnect on missed ping', err));
      return;
    }
    this.trackingPings();
  }
  onBail() {
    trackerService.track('serverSentEvent', 'disconnections');
    loggerService.warn('SSE: Bailing on eventSource connection!');
    store.commit('event/avroom/setAllChannelsToLeft');
    store.commit('eventsource/setEventSourceStatus', 'bailed');
    processUpdateConnectionStatus({ status: 'bailed', firstConnectDone: store.state.eventsource.firstConnectDone });
  }
}
